import React, { Suspense, lazy } from 'react'
import { useUIVariant } from '@agion/ui/ui-variant'
import { Layout } from './Layout'
import { LoadingSpinner } from './LoadingSpinner'

// The next shell is its own chunk; a member on the current chat never downloads it.
const NextChatShell = lazy(() => import('../../next/shell/NextChatShell'))

function ShellHold() {
  return (
    <div className="flex h-screen items-center justify-center" data-testid="ui-variant-hold">
      <LoadingSpinner size="large" />
    </div>
  )
}

/**
 * Picks the chat shell from the server resolution (PLAN-20260929 slice 2,
 * agion/Agion#3210). Neither shell, nor the page inside it, mounts before the
 * server has answered: both shells list runs on mount, and the current
 * Sidebar's list is the organisation's whole run list (F-ACC-03,
 * agion/Agion#3003). While the question is open a neutral hold renders. The
 * new chat renders for a next answer and for every failed one (no signed-in
 * member, an error, a refusal, a malformed answer, or no answer within
 * UI_VARIANT_RESOLUTION_TIMEOUT_MS); the current Layout renders only on the
 * server's legacy answer. A bare frame (the refusal page) keeps the page
 * without the chat rail on either variant.
 */
export function VariantLayout({ children, frame = 'rail' }: { children: React.ReactNode; frame?: 'rail' | 'bare' }) {
  const { variant, status } = useUIVariant()
  if (status === 'resolving') {
    return <ShellHold />
  }
  if (variant === 'legacy') {
    // A bare page (the refusal page) has no chat frame on the current chat.
    return frame === 'bare' ? <>{children}</> : <Layout>{children}</Layout>
  }
  return (
    <Suspense fallback={<ShellHold />}>
      <NextChatShell bare={frame === 'bare'}>{children}</NextChatShell>
    </Suspense>
  )
}
